// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Shared song indexing core used by browser workers and server-side code.
 *
 * Usage:
 * - Browser: SongIndexClient talks to song-index-worker, which owns a store.
 * - Server: create a SongIndexStore with a filesystem-backed FetchJson.
 *
 * The store owns one immutable IndexedSong array and secondary indexes point to
 * those same objects, avoiding duplicate song payloads in memory.
 */

import { groupSongResults, type GroupedSongResult } from "./song-results";

export type AudioLink = {
  url: string;
  title?: string | null;
  key?: string | null;
  pitch?: string | null;
};

export type Translation = {
  language?: string | null;
  lyrics: string[][];
};

export type SongInfo = {
  title: string;
  id?: string | null;
  number?: string | null;
  page?: number | null;
  chapter_title?: string | null;
  alt_titles?: string[];
  audio_links?: AudioLink[];
  lyrics?: string[][] | null;
  translations?: Translation[];
  options?: {
    by?: string | null;
    key?: string | null;
    off?: string | null;
    ex?: string | null;
    gk?: string | null;
    tags?: string | string[] | null;
  };
};

export type ChapterInfo = {
  title: string;
  songs: SongInfo[];
};

export type BookJson = {
  book_info: {
    variant?: string | null;
  };
  chapters: ChapterInfo[];
  songs_without_chapter: SongInfo[];
};

export type BookInit = {
  bookId: number;
  viewId: string;
  jsonUrl: string;
  sourceId: string;
  variant: string;
};

export type EntityToggles = {
  titles: boolean;
  lyrics: boolean;
  translations: boolean;
  chapters: boolean;
  authors: boolean;
  offered_to: boolean;
  extra_info: boolean;
  tags: boolean;
  numbers: boolean;
  ids: boolean;
};

export type SearchOpts = {
  variants: string[];
  sources: string[];
  entities: EntityToggles;
};

export type SearchHit = SongDetails & {
  matches: {
    title: boolean;
    lyrics: boolean;
    translations: boolean;
    author: boolean;
    id: boolean;
    number: boolean;
    chapter: boolean;
    offered_to: boolean;
    extra_info: boolean;
    tags: boolean;
  };
  hasAudio: boolean;
};

/** Exact stable-ID results suitable for the shared song presentation. */
export type ExactSongIdResult = { hits: SearchHit[]; groups: GroupedSongResult[] };

export type SongDetails = {
  ref: number;
  bookId: number;
  viewId: string;
  sourceId: string;
  variant: string;
  title: string;
  number: string | null;
  page: number | null;
  chapterTitle: string | null;
  songId: string | null;
  author: string | null;
  key: string | null;
  offeredTo: string | null;
  extraInfo: string | null;
  goodKeys: string | null;
  tags: string | null;
  audioLinks: AudioLink[];
  lyrics: string[][] | null;
  translations: Translation[];
};

export type IndexedSong = Readonly<{
  ref: number;
  bookId: number;
  viewId: string;
  sourceId: string;
  variant: string;
  title: string;
  titleNorm: string;
  chapterTitle: string | null;
  songId: string | null;
  number: string | null;
  page: number | null;
  author: string | null;
  key: string | null;
  offeredTo: string | null;
  extraInfo: string | null;
  goodKeys: string | null;
  tags: string | null;
  authorNorm: string;
  idNorm: string;
  numberNorm: string;
  chapterTitleNorm: string;
  offeredToNorm: string;
  extraInfoNorm: string;
  tagsNorm: string;
  hasAudio: boolean;
  audioLinks: AudioLink[];
  lyrics: string[][] | null;
  lyricsPlainNorm: string;
  translations: Translation[];
  translationsPlainNorm: string;
}>;

export type SongIndexStats = Readonly<{
  variants: string[];
  sources: string[];
  songCount: number;
  bookCount: number;
  errorCount: number;
  songIdCount: number;
}>;

export type SongIndexEntry = Readonly<{
  songId: string;
  title: string;
  author: string | null;
}>;

export type InitProgress = Readonly<{ loaded: number; total: number }>;

export type SongIndexInitResult = SongIndexStats & Readonly<{ skipped: boolean }>;

export type SongIndexInitOptions = Readonly<{
  force?: boolean;
  onProgress?: (progress: InitProgress) => void;
}>;

export type FetchJson = (url: string) => Promise<BookJson>;

/** Normalizes text for forgiving search and id lookups. */
export function normalizeForSearch(s: string): string {
  const base = String(s ?? "").replace(/"/g, "");
  return base
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Flattens nested verse arrays into one whitespace-normalized text string. */
export function flattenVerses(verses: string[][] | null | undefined): string {
  if (!Array.isArray(verses) || verses.length === 0) return "";
  const raw = verses.map(v => (Array.isArray(v) ? v.join(" ") : "")).join(" ");
  return raw.replace(/\s+/g, " ").trim();
}

/** Shortens normalized text at a word boundary, retaining an indivisible first word. */
export function wordExcerpt(text: string, maxLength: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  const boundary = normalized.lastIndexOf(" ", maxLength);
  const firstWordEnd = normalized.indexOf(" ");
  return `${normalized.slice(0, boundary > 0 ? boundary : firstWordEnd > 0 ? firstWordEnd : normalized.length)}...`;
}

/** Uses the original title, then a complete-word lyrics excerpt, then stable identity. */
export function displaySongTitle(song: {
  title?: string;
  lyrics?: string[][] | null;
  songId?: string | null;
}): string {
  return song.title?.trim() || wordExcerpt(flattenVerses(song.lyrics), 100) || song.songId || "";
}

/** Default browser JSON loader used by the worker-backed client store. */
export async function fetchBookJson(url: string): Promise<BookJson> {
  const request = new Request(url, { cache: "no-cache" });
  const resp = await fetch(request).catch(async err => {
    const cached =
      typeof caches !== "undefined"
        ? await caches.match(url, { ignoreSearch: false, ignoreVary: true })
        : undefined;
    if (cached) return cached;
    throw err;
  });
  if (!resp.ok) throw new Error(`Failed to fetch ${url} (${resp.status} ${resp.statusText})`);
  return (await resp.json()) as BookJson;
}

function readonlySong(song: Omit<IndexedSong, "ref">, ref: number): IndexedSong {
  return Object.freeze({ ...song, ref });
}

/**
 * Converts one book JSON document into immutable IndexedSong objects.
 * The returned nextRef lets callers assign unique refs across many books.
 */
export function buildIndexedSongsForBook(
  init: BookInit,
  json: BookJson,
  nextRefStart = 1,
): { songs: IndexedSong[]; variant: string | null; sourceId: string; nextRef: number } {
  const variantRaw = String(json?.book_info?.variant ?? "").trim();
  const variant = (init.variant || variantRaw || "default").trim();
  const out: IndexedSong[] = [];
  let nextRef = nextRefStart;

  const pushSong = (song: SongInfo, chapterTitleFallback: string | null): void => {
    const title = String(song?.title ?? "").trim();
    const rawId = (song as any)?.id;
    const songId = typeof rawId === "string" ? rawId.trim() || null : null;
    if (!title && !songId) return;
    const number = String((song as any)?.number ?? "").trim() || null;
    const rawPage = (song as any)?.page;
    const page = typeof rawPage === "number" || typeof rawPage === "string" ? Number(rawPage) : NaN;
    const pageNum = Number.isInteger(page) && page > 0 ? page : null;

    const chapterTitleRaw = String((song as any)?.chapter_title ?? "").trim();
    const chapterTitle = chapterTitleRaw || chapterTitleFallback;

    const rawOptions = (song as any)?.options ?? {};
    const author = String(rawOptions.by ?? "").trim() || null;
    const songKey = String(rawOptions.key ?? "").trim() || null;
    const offeredTo = String(rawOptions.off ?? "").trim() || null;
    const extraInfo = String(rawOptions.ex ?? "").trim() || null;
    const goodKeys = String(rawOptions.gk ?? "").trim() || null;

    const rawTags = rawOptions.tags;
    let tags: string | null = null;
    if (Array.isArray(rawTags)) {
      tags = rawTags
        .map((x: unknown) => String(x ?? "").trim())
        .filter((x: string) => x.length > 0)
        .join(", ");
      if (!tags) tags = null;
    } else {
      const tagStr = String(rawTags ?? "").trim();
      tags = tagStr || null;
    }

    const lyrics = Array.isArray((song as any)?.lyrics)
      ? ((song as any).lyrics as string[][])
      : null;
    const translations = Array.isArray((song as any)?.translations)
      ? ((song as any).translations as Translation[])
      : [];
    const audioLinks = Array.isArray((song as any)?.audio_links)
      ? ((song as any).audio_links as AudioLink[])
      : [];

    const lyricsPlain = flattenVerses(lyrics);
    const translationsPlain = translations
      .map(tr => flattenVerses(tr?.lyrics))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    const authorNorm = normalizeForSearch(author || "");
    const idNorm = normalizeForSearch(songId || "");
    const numberNorm = normalizeForSearch(number || "");
    const chapterTitleNorm = normalizeForSearch(chapterTitleRaw || "");
    const offeredToNorm = normalizeForSearch(offeredTo || "");
    const extraInfoNorm = normalizeForSearch(extraInfo || "");
    const tagsNorm = normalizeForSearch(tags || "");

    out.push(
      readonlySong(
        {
          bookId: init.bookId,
          viewId: init.viewId,
          sourceId: init.sourceId,
          variant,
          title,
          titleNorm: normalizeForSearch(title),
          chapterTitle: chapterTitle ? chapterTitle : null,
          songId,
          number,
          page: pageNum,
          author,
          key: songKey,
          offeredTo,
          extraInfo,
          goodKeys,
          tags,
          authorNorm,
          idNorm,
          numberNorm,
          chapterTitleNorm,
          offeredToNorm,
          extraInfoNorm,
          tagsNorm,
          hasAudio: audioLinks.length > 0,
          audioLinks,
          lyrics,
          lyricsPlainNorm: normalizeForSearch(lyricsPlain),
          translations,
          translationsPlainNorm: normalizeForSearch(translationsPlain),
        },
        nextRef++,
      ),
    );
  };

  for (const ch of json?.chapters ?? []) {
    const chTitle = String(ch?.title ?? "").trim() || null;
    for (const s of ch?.songs ?? []) pushSong(s, chTitle);
  }

  for (const s of json?.songs_without_chapter ?? []) pushSong(s, null);

  return { songs: out, variant, sourceId: init.sourceId, nextRef };
}

function stableInitKey(books: readonly BookInit[]): string {
  return JSON.stringify(
    books.map(b => ({
      bookId: b.bookId,
      viewId: b.viewId,
      jsonUrl: b.jsonUrl,
      sourceId: b.sourceId,
      variant: b.variant,
    })),
  );
}

/**
 * In-memory song index with lazy/reusable initialization.
 *
 * Call init() once with BookInit entries before querying. Repeated init() with
 * the same book set is ignored unless force is true; concurrent callers share
 * the same pending promise. Query methods are synchronous after initialization.
 */
export class SongIndexStore {
  private songsValue: readonly IndexedSong[] = [];
  private songIdIndexValue = new Map<string, readonly IndexedSong[]>();
  private exactSongIdIndexValue = new Map<string, readonly IndexedSong[]>();
  private refIndexValue = new Map<number, IndexedSong>();
  private variantsValue: string[] = [];
  private sourcesValue: string[] = [];
  private bookCountValue = 0;
  private errorCountValue = 0;
  private readyValue = false;
  private initKey: string | null = null;
  private initPromise: Promise<SongIndexInitResult> | null = null;

  /** Creates a store; pass a custom FetchJson on the server or in tests. */
  constructor(private readonly fetchJson: FetchJson = fetchBookJson) {}

  /** True after a successful init/rebuild. */
  get ready(): boolean {
    return this.readyValue;
  }

  /** True while init/rebuild is currently running. */
  get pending(): boolean {
    return !!this.initPromise;
  }

  /** The single immutable song payload array owned by this store. */
  get songs(): readonly IndexedSong[] {
    return this.songsValue;
  }

  /** Snapshot of available filters and index sizes. */
  get stats(): SongIndexStats {
    return Object.freeze({
      variants: [...this.variantsValue],
      sources: [...this.sourcesValue],
      songCount: this.songsValue.length,
      bookCount: this.bookCountValue,
      errorCount: this.errorCountValue,
      songIdCount: this.songIdIndexValue.size,
    });
  }

  /** Initializes or rebuilds the store from book JSON files. */
  async init(
    books: readonly BookInit[],
    opts: SongIndexInitOptions = {},
  ): Promise<SongIndexInitResult> {
    const key = stableInitKey(books);
    if (!opts.force && this.readyValue && this.initKey === key) {
      return { ...this.stats, skipped: true };
    }
    if (!opts.force && this.initPromise) return this.initPromise;

    this.initPromise = this.rebuild(books, key, opts);
    try {
      return await this.initPromise;
    } finally {
      if (this.initPromise) this.initPromise = null;
    }
  }

  /** Waits for readiness, optionally starting init if books are supplied. */
  async ensureReady(books?: readonly BookInit[]): Promise<void> {
    if (this.readyValue) return;
    if (this.initPromise) {
      await this.initPromise;
      return;
    }
    if (!books) throw new Error("Song index has not been initialized.");
    await this.init(books);
  }

  /** Searches matching appearances, limiting unique songs without splitting their groups. */
  search(
    query: string,
    opts: SearchOpts,
  ): { queryNorm: string; hits: SearchHit[]; groups: GroupedSongResult[]; truncated: boolean } {
    const queryNorm = normalizeForSearch(query);
    let queryNormForNumbers = queryNorm;
    if (queryNormForNumbers.endsWith("."))
      queryNormForNumbers = queryNormForNumbers.replace(/\.+$/g, "");
    const isNumericNumberQuery = /^\d+$/.test(queryNormForNumbers);
    const MAX_RESULTS = 200;

    if (!this.readyValue || !queryNorm)
      return { queryNorm, hits: [], groups: [], truncated: false };

    const allowedVariants = new Set(
      (opts?.variants ?? []).map(x => String(x).trim()).filter(Boolean),
    );
    const allowedSources = new Set(
      (opts?.sources ?? []).map(x => String(x).trim()).filter(Boolean),
    );
    const ent: EntityToggles = {
      titles: opts?.entities?.titles ?? true,
      lyrics: opts?.entities?.lyrics ?? true,
      translations: opts?.entities?.translations ?? true,
      authors: opts?.entities?.authors ?? true,
      ids: opts?.entities?.ids ?? true,
      numbers: opts?.entities?.numbers ?? true,
      chapters: opts?.entities?.chapters ?? true,
      offered_to: opts?.entities?.offered_to ?? true,
      extra_info: opts?.entities?.extra_info ?? true,
      tags: opts?.entities?.tags ?? true,
    };

    const hits: SearchHit[] = [];
    const admittedIds = new Set<string>();
    let songCount = 0;
    let truncated = false;

    for (const s of this.songsValue) {
      if (!allowedVariants.has(s.variant)) continue;
      if (allowedSources.size > 0 && !allowedSources.has(s.sourceId)) continue;

      const matchTitle = !!ent.titles && s.titleNorm.includes(queryNorm);
      const matchLyrics = !!ent.lyrics && s.lyricsPlainNorm.includes(queryNorm);
      const matchTranslations = !!ent.translations && s.translationsPlainNorm.includes(queryNorm);
      const matchAuthor = !!ent.authors && s.authorNorm.includes(queryNorm);
      const matchId = !!ent.ids && s.idNorm.includes(queryNorm);
      const matchNumber =
        !!ent.numbers && isNumericNumberQuery && s.numberNorm === queryNormForNumbers;
      const matchChapter = !!ent.chapters && s.chapterTitleNorm.includes(queryNorm);
      const matchOfferedTo = !!ent.offered_to && s.offeredToNorm.includes(queryNorm);
      const matchExtraInfo = !!ent.extra_info && s.extraInfoNorm.includes(queryNorm);
      const matchTags = !!ent.tags && s.tagsNorm.includes(queryNorm);

      if (
        !matchTitle &&
        !matchLyrics &&
        !matchTranslations &&
        !matchAuthor &&
        !matchId &&
        !matchNumber &&
        !matchChapter &&
        !matchOfferedTo &&
        !matchExtraInfo &&
        !matchTags
      )
        continue;

      const songId = s.songId?.trim() || null;
      if (songId === null || !admittedIds.has(songId)) {
        if (songCount >= MAX_RESULTS) {
          truncated = true;
          continue;
        }
        songCount++;
        if (songId !== null) admittedIds.add(songId);
      }

      hits.push({
        ...songDetailsFromIndexedSong(s),
        matches: {
          title: matchTitle,
          lyrics: matchLyrics,
          translations: matchTranslations,
          author: matchAuthor,
          id: matchId,
          number: matchNumber,
          chapter: matchChapter,
          offered_to: matchOfferedTo,
          extra_info: matchExtraInfo,
          tags: matchTags,
        },
        hasAudio: s.hasAudio,
      });
    }

    return { queryNorm, hits, groups: groupSongResults(hits), truncated };
  }

  /** Returns full display data for a search result ref. */
  getSongDetailsByRef(ref: number): SongDetails | null {
    if (!this.readyValue) return null;
    const n = Math.floor(Number(ref));
    if (!Number.isFinite(n) || n < 1) return null;
    const s = this.refIndexValue.get(n) ?? null;
    return s ? songDetailsFromIndexedSong(s) : null;
  }

  /** Returns all indexed variants with the given song id; objects are shared with songs. */
  getSongsBySongId(songId: string): readonly IndexedSong[] {
    if (!this.readyValue) return [];
    const key = normalizeForSearch(songId);
    return this.songIdIndexValue.get(key) ?? [];
  }

  /** Looks up a stable ID exactly, without search/PDF normalization or query trimming. */
  lookupExactSongId(songId: string): ExactSongIdResult {
    const songs = this.readyValue ? (this.exactSongIdIndexValue.get(songId) ?? []) : [];
    const hits: SearchHit[] = songs.map(song => ({
      ...songDetailsFromIndexedSong(song),
      hasAudio: song.hasAudio,
      matches: {
        title: false,
        lyrics: false,
        translations: false,
        author: false,
        id: true,
        number: false,
        chapter: false,
        offered_to: false,
        extra_info: false,
        tags: false,
      },
    }));
    return { hits, groups: groupSongResults(hits) };
  }

  /** Returns normalized song ids known by the song-id index. */
  getAllSongIds(): string[] {
    return Array.from(this.songIdIndexValue.keys()).sort((a, b) => a.localeCompare(b));
  }

  /** Returns one display entry per song id, using the first indexed variant as representative. */
  getSongIndexEntries(): SongIndexEntry[] {
    return Array.from(this.exactSongIdIndexValue.values())
      .map(songs => songs[0])
      .filter((s): s is IndexedSong => !!s)
      .map(s => ({ songId: s.songId ?? "", title: displaySongTitle(s), author: s.author }))
      .filter(s => !!s.songId && !!s.title);
  }

  /** Rebuilds all primary and secondary indexes atomically. */
  private async rebuild(
    books: readonly BookInit[],
    key: string,
    opts: SongIndexInitOptions,
  ): Promise<SongIndexInitResult> {
    this.readyValue = false;
    const total = books.length;
    let loaded = 0;
    let errorCount = 0;
    let successCount = 0;
    let nextRef = 1;
    const allSongs: IndexedSong[] = [];
    const variantSet = new Set<string>();
    const sourceSet = new Set<string>();

    for (const b of books) {
      try {
        const url = String(b.jsonUrl ?? "").trim();
        if (!url) continue;
        const json = await this.fetchJson(url);
        const one = buildIndexedSongsForBook(b, json, nextRef);
        successCount++;
        nextRef = one.nextRef;
        if (one.variant) variantSet.add(one.variant);
        if (one.sourceId) sourceSet.add(one.sourceId);
        allSongs.push(...one.songs);
      } catch {
        errorCount++;
      } finally {
        loaded++;
        opts.onProgress?.({ loaded, total });
      }
    }

    if (books.length > 0 && successCount === 0) {
      throw new Error("No song index JSON files could be loaded.");
    }

    const songIdIndex = new Map<string, IndexedSong[]>();
    const exactSongIdIndex = new Map<string, IndexedSong[]>();
    const refIndex = new Map<number, IndexedSong>();
    for (const s of allSongs) {
      refIndex.set(s.ref, s);
      if (!s.songId) continue;
      const exactSongs = exactSongIdIndex.get(s.songId) ?? [];
      exactSongs.push(s);
      exactSongIdIndex.set(s.songId, exactSongs);
      const idKey = normalizeForSearch(s.songId);
      if (!idKey) continue;
      const arr = songIdIndex.get(idKey) ?? [];
      arr.push(s);
      songIdIndex.set(idKey, arr);
    }

    this.songsValue = Object.freeze([...allSongs]);
    this.refIndexValue = refIndex;
    this.songIdIndexValue = new Map(
      Array.from(songIdIndex.entries()).map(([id, arr]) => [
        id,
        Object.freeze([...arr]) as readonly IndexedSong[],
      ]),
    );
    this.exactSongIdIndexValue = new Map(
      Array.from(exactSongIdIndex.entries()).map(([id, songs]) => [id, Object.freeze(songs)]),
    );
    this.variantsValue = Array.from(variantSet).sort((a, b) => a.localeCompare(b));
    this.sourcesValue = Array.from(sourceSet).sort((a, b) => a.localeCompare(b));
    this.bookCountValue = books.length;
    this.errorCountValue = errorCount;
    this.initKey = key;
    this.readyValue = true;

    return { ...this.stats, skipped: false };
  }
}

/** Projects an IndexedSong into the public details DTO used by worker clients. */
export function songDetailsFromIndexedSong(s: IndexedSong): SongDetails {
  return {
    ref: s.ref,
    bookId: s.bookId,
    viewId: s.viewId,
    sourceId: s.sourceId,
    variant: s.variant,
    title: s.title,
    number: s.number,
    page: s.page,
    chapterTitle: s.chapterTitle,
    songId: s.songId,
    author: s.author,
    key: s.key,
    offeredTo: s.offeredTo,
    extraInfo: s.extraInfo,
    goodKeys: s.goodKeys,
    tags: s.tags,
    audioLinks: s.audioLinks,
    lyrics: s.lyrics,
    translations: s.translations,
  };
}
